import { OptimizelyProduct, Source } from '../types/DataSyncItem';
import { INCOMPLETE } from '../types/FieldMapping';
import {
  FIELD_PATH_SEPARATOR,
  FieldDefinition,
} from '../types/FieldDefinition';
import { FieldMappingEntry } from '../types/DataSyncViewModel';

const hasArrayFields = (
  field: FieldDefinition
): field is FieldDefinition & { array: { fields: FieldDefinition[] } } => {
  return (
    field.array !== undefined &&
    typeof field.array === 'object' &&
    'fields' in field.array
  );
};

export const findFieldByPath = (
  fields: FieldDefinition[],
  path: string
): FieldDefinition | undefined => {
  const pathParts = path.split('/');

  if (pathParts.length === 1) {
    return fields.find((field) => field.name === path);
  }

  const [firstPart, ...remainingParts] = pathParts;
  const parentField = fields.find((field) => field.name === firstPart);

  if (!parentField) {
    return undefined;
  }

  // Handle nested object fields
  if (parentField.object_schema?.fields) {
    return findFieldByPath(
      parentField.object_schema.fields,
      remainingParts.join(FIELD_PATH_SEPARATOR)
    );
  }

  // Handle arrays with object schemas
  if (hasArrayFields(parentField)) {
    return findFieldByPath(
      parentField.array.fields,
      remainingParts.join(FIELD_PATH_SEPARATOR)
    );
  }

  return undefined;
};

/**
 * Whether a mapping may be disabled.
 *
 * `disabled` means "omit this field from the generated Content Graph content type". That is
 * only meaningful for a field that exists purely because the source schema was cloned 1:1.
 * Once a destination is pointed at a different source -- manually or by auto-map -- it is a
 * deliberate mapping and disabling it silently discards the user's intent (OCP-2313).
 *
 * The `isCloned` flag alone is not enough: it is re-applied to every non-base-contract
 * mapping when an inherited schema is reloaded, including ones the user has re-pointed.
 *
 * @param mapping The mapping entry to check
 * @returns True when the mapping is a 1:1 clone of a source field
 */
export function isOneToOneClonedMapping(mapping: FieldMappingEntry): boolean {
  return (
    !!mapping.isCloned &&
    !!mapping.sourceField &&
    mapping.sourceField === mapping.destinationField
  );
}

export function resetSourceFieldMappings(
  field_mappings: FieldMappingEntry[]
): FieldMappingEntry[] {
  return field_mappings.map(({ destinationField, disabled }) => ({
    destinationField,
    sourceField: INCOMPLETE,
    disabled,
  }));
}

export function resetDestinationFieldMappings(
  field_mappings: FieldMappingEntry[]
): FieldMappingEntry[] {
  return field_mappings.map(({ sourceField, disabled }) => ({
    destinationField: INCOMPLETE,
    sourceField,
    disabled,
  }));
}

/**
 * Filters out system fields that should not be shown in the field mappings UI.
 * Currently filters out: _metadata
 */
export const filterSystemFields = (fields: FieldDefinition[]): FieldDefinition[] => {
  return fields.filter((field) => field.name !== '_metadata');
};

export function getObjectLabel(product: string): string {
  return product === OptimizelyProduct.CGR ? 'Content Type' : 'Object';
}

export function getNotificationLabelForObject(product: string): string {
  return product === OptimizelyProduct.CGR ? 'a content type' : 'an object';
}

/**
 * Source instance id for the Graph destination schema endpoint. For HUB_APP this equals
 * `source.object.app_id`, but `instance.id` is populated as soon as the app is picked.
 */
export function getSourceProductInstanceId(source?: Source): string | undefined {
  return source?.instance?.id;
}

/**
 * Whether the source section is filled enough to resolve a Graph source id. HUB (OCP Database)
 * has no instance, so a product is enough; everything else also needs an instance id.
 */
export function isSourceFilled(source?: Source): boolean {
  if (!source?.product) {
    return false;
  }
  if (source.product === OptimizelyProduct.HUB) {
    return true;
  }
  return !!source.instance?.id;
}

export function getSchemaChangesMessage(
  mappings: FieldMappingEntry[],
  sourceFields: FieldDefinition[],
  destinationFields: FieldDefinition[]
): string | null {
  if (mappings.length === 0) {
    return null;
  }

  const orphanedDest = mappings
    .filter((m) => m.destinationField && !findFieldByPath(destinationFields, m.destinationField))
    .map((m) => m.destinationField);

  const orphanedSource = mappings
    .filter((m) => m.sourceField && !findFieldByPath(sourceFields, m.sourceField))
    .map((m) => m.sourceField);

  if (orphanedDest.length === 0 && orphanedSource.length === 0) {
    return null;
  }

  const parts: string[] = [];
  if (orphanedDest.length > 0) {
    parts.push(`destination field(s) ${orphanedDest.join(', ')}`);
  }
  if (orphanedSource.length > 0) {
    parts.push(`source field(s) ${orphanedSource.join(', ')}`);
  }

  return `Schema changes detected: ${parts.join(' and ')} no longer exist in the current schema.`;
}

export function filterOrphanedMappings(
  mappings: FieldMappingEntry[],
  destinationFields: FieldDefinition[]
): FieldMappingEntry[] {
  return mappings.filter((m) =>
    findFieldByPath(destinationFields, m.destinationField)
  );
}
